import { useState } from "react";
import { Reveal, SectionLabel } from "@/components/site/bits";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Send, CalendarClock, MessageCircle, Phone, MapPin } from "lucide-react";

const WA = "https://wa.me/917042141004";

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", requirement: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (source) => {
    if (!form.name || !form.email) { toast.error("Please enter your name and email."); return; }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source: "contact", meta: { intent: source } });
      toast.success(source === "proposal" ? "Proposal request sent! We'll be in touch soon." : "Consultation request received!");
      setForm({ name: "", company: "", email: "", phone: "", requirement: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="contact" className="relative py-28 px-6" data-testid="contact">
      <div className="mx-auto max-w-6xl grid lg:grid-cols-5 gap-10">
        <Reveal className="lg:col-span-2">
          <SectionLabel>Let's Build</SectionLabel>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
            Start your <span className="text-gradient">AI journey</span>
          </h2>
          <p className="mt-4 text-white/60 leading-relaxed">Tell us about your business. We'll craft a tailored AI & automation proposal — no jargon, just growth.</p>

          <div className="mt-8 space-y-4">
            <a href={WA} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-2xl glass p-4 hover:border-[#00FFB2]/40 transition-colors" data-testid="contact-whatsapp">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-[#00FFB2]/15"><MessageCircle className="h-5 w-5 text-[#00FFB2]" /></span>
              <div><div className="text-sm font-semibold">WhatsApp</div><div className="text-xs text-white/50">+91 7042141004</div></div>
            </a>
            <div className="flex items-center gap-3 rounded-2xl glass p-4">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-[#00E5FF]/15"><Phone className="h-5 w-5 text-[#00E5FF]" /></span>
              <div><div className="text-sm font-semibold">Call / Message</div><div className="text-xs text-white/50">+91 7042141004</div></div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl glass p-4">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-[#7B61FF]/15"><MapPin className="h-5 w-5 text-[#7B61FF]" /></span>
              <div><div className="text-sm font-semibold">Location</div><div className="text-xs text-white/50">India · GST 06DDIPS3975N1ZN</div></div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-3">
          <div className="rounded-3xl glass p-8" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name"><Input value={form.name} onChange={set("name")} data-testid="contact-name" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" /></Field>
              <Field label="Company"><Input value={form.company} onChange={set("company")} data-testid="contact-company" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" /></Field>
              <Field label="Email"><Input type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" /></Field>
              <Field label="Phone"><Input value={form.phone} onChange={set("phone")} data-testid="contact-phone" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" /></Field>
            </div>
            <div className="mt-4">
              <Field label="Requirement"><Textarea rows={4} value={form.requirement} onChange={set("requirement")} data-testid="contact-requirement" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF] resize-none" placeholder="What would you like to build or automate?" /></Field>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button disabled={loading} onClick={() => submit("proposal")} data-testid="contact-proposal-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#7B61FF] py-3.5 font-semibold text-[#0B1220] hover:scale-[1.02] transition-transform disabled:opacity-50">
                <Send className="h-4 w-4" /> Get Proposal
              </button>
              <button disabled={loading} onClick={() => submit("consultation")} data-testid="contact-consult-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full glass py-3.5 font-semibold hover:border-[#00E5FF]/50 transition-colors disabled:opacity-50">
                <CalendarClock className="h-4 w-4 text-[#00FFB2]" /> Schedule Consultation
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-white/50">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);
